// src/components/SignupForm.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './SignupForm.css';
import config from '../config';
import { countries } from '../data/countries';

const SignupForm = ({ onSwitchToLogin, onBack }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [country, setCountry] = useState('');

  const { login } = useAuth();

  // Helper function to get currency from country code
  const getCurrencyFromCountry = (countryCode) => {
    // If country is India, return INR
    if (countryCode === 'IN') {
      return 'INR';
    }
    // For all other countries, return USD
    return 'USD';
  };


  const validateForm = () => {
    if (!username.trim()) {
      setError('Username is required');
      return false;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }

    if (!email.trim()) {
      setError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!password) {
      setError('Password is required');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {

      const currency = getCurrencyFromCountry(country);

      // First, get CSRF token
      await fetch(`${config.API_BASE_URL}/api/csrf/`, {
        method: 'GET',
        credentials: 'include',
      });

      // Create user via Django's default user creation
      const response = await fetch(`${config.API_BASE_URL}/api/auth/signup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username,
          email: email,
          password: password,
          currency: currency
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          // Handle validation errors
          if (data.username) {
            throw new Error(data.username[0]);
          } else if (data.email) {
            throw new Error(data.email[0]);
          } else if (data.password) {
            throw new Error(data.password[0]);
          } else {
            throw new Error(data.message || 'Signup failed');
          }
        } else {
          throw new Error(data.message || 'Signup failed');
        }
      }

      setSuccess(true);

      // Auto login after successful signup
      setTimeout(async () => {
        const loginResult = await login(username, password);
        if (loginResult.success) {
          // Login successful - parent component will handle redirect
          window.location.reload(); // Refresh to update auth state
        }
      }, 1500);

    } catch (error) {
      // console.error('Signup error:', error);
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        {/* Back Button - calls onBack when clicked */}
        {onBack && (
          <button onClick={onBack} className="back-to-landing" type="button">
            ← Back to Home
          </button>
        )}
        <div className="signup-header">
          <span className="signup-icon">📝</span>
          <h2>Create Account</h2>
          <p>Join {config.APP_NAME}</p>
        </div>

        {error && (
          <div className="signup-error">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="signup-success">
            <span className="success-icon">✓</span>
            <p>Account created successfully! Redirecting...</p>
          </div>
        )}

        <form onSubmit={handleSignup} className="signup-form">
          <div className="form-group">
            <label htmlFor="username">
              Username <span className="required-star">*</span>
            </label>
            <div className="input-wrapper">
              <span className="input-icon">👤</span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                placeholder="Choose a username"
                autoComplete="username"
              />
            </div>
            <div className="input-hint">Minimum 3 characters</div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required-star">*</span>
            </label>
            <div className="input-wrapper">
              <span className="input-icon">📧</span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required-star">*</span>
            </label>
            <div className="input-wrapper">
              <span className="input-icon">🔒</span>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Create a password"
                autoComplete="new-password"
              />
            </div>
            <div className="input-hint">Minimum 6 characters</div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required-star">*</span>
            </label>
            <div className="input-wrapper">
              <span className="input-icon">🔒</span>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>
          </div>

          {/* Country dropdown JSX */}
          <div className="form-group">
            <label htmlFor="country">
              Country <span className="required-star">*</span>
            </label>
            <div className="input-wrapper">
              <span className="input-icon">🌍</span>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                disabled={loading}
                className="country-select"
              >
                <option value="">Select your country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="input-hint">Please select Other if country is not listed</div>
          </div>

          <button
            type="submit"
            className="signup-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Creating Account...
              </>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="switch-link"
              disabled={loading}
            >
              Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;